import React from 'react';
import UmlPackageShape from '../../Types/UmlPackageShape.js';
import UmlClassShape from '../../Types/UmlClassShape.js';
import UmlCommentShape from '../../Types/UmlCommentShape.js';
import UmlIconShape from '../../Types/UmlIconShape.js';
import UmlSeqParticipantShape from '../../Types/UmlSeqParticipantShape.js';
import UmlSeqLifelineShape from '../../Types/UmlSeqLifelineShape.js';
import UmlSeqMessageShape from '../../Types/UmlSeqMessageShape.js';
import UmlSeqActivationShape from '../../Types/UmlSeqActivationShape.js';
import UmlPackageWidget from './UmlPackageWidget.jsx';
import UmlClassWidget from './UmlClassWidget.jsx';
import UmlCommentWidget from './UmlCommentWidget.jsx';
import UmlIconWidget from './UmlIconWidget.jsx';
import UmlSeqParticipantWidget from './UmlSeqParticipantWidget.jsx';
import UmlSeqLifelineWidget from './UmlSeqLifelineWidget.jsx';
import UmlSeqMessageWidget from './UmlSeqMessageWidget.jsx';
import UmlSeqActivationWidget from './UmlSeqActivationWidget.jsx';

class ShapeTreeRenderer {
    /**
     * Splits the shape tree into two layers so render order can put connectors
     * BEHIND UmlClass/UmlComment shapes but IN FRONT OF UmlPackage shapes (see
     * UmlCanvas.jsx: packageElements, then <UmlConnectorLayer>, then
     * otherElements). Packages contribute only their own border/label here —
     * their children (further classes AND further nested packages) are walked
     * recursively and end up in whichever list they belong to, at the same
     * stackIndex depth-model.
     * @param {Array} shapes
     * @param {Function} moveShape
     * @param {Function} resizeShape
     * @param {Function} onConnectorDragStart
     * @param {Function} renameShape
     * @param {string|null} selectedId
     * @param {Function} selectShape
     * @param {Function} onBringToFront
     * @param {Function} onShapeCollapseToggled
     * @param {Function} onShapeIconKindChanged
     * @param {Function} toWorldPoint
     * @param {object} draggingShapeIdRef
     * @param {Node|null} titleLayerNode - DOM node for the portaled package-title layer (see UmlCanvas.jsx .uml-package-title-layer), null until first mount
     * @param {Set<string>} selectedShapeIds - shape multi-selection, see UmlSelectionEngine
     * @param {Function} onShapeShiftSelected - toggles multi-selection membership
     * @param {Function} getShapeById
     * @param {number} parentZIndex
     * @param {Function} [onSeqMessageLabelMoved] - moves a UmlSeqMessageShape's own draggable label offset (see UmlSeqMessageWidget)
     * @returns {{packageElements: Array, otherElements: Array}}
     */
    static renderLayered(
        shapes,
        moveShape,
        resizeShape,
        onConnectorDragStart,
        renameShape,
        selectedId,
        selectShape,
        onBringToFront,
        onShapeCollapseToggled,
        onShapeIconKindChanged,
        toWorldPoint,
        draggingShapeIdRef,
        titleLayerNode,
        selectedShapeIds,
        onShapeShiftSelected,
        getShapeById,
        parentZIndex = 0,
        onSeqMessageLabelMoved,
    ) {
        const packageElements = [];
        const otherElements = [];

        function walk(shapeList, zIndexFloor) {
            shapeList.forEach(function walkOne(shape) {
                const stackIndex = Math.max(shape.zIndex, zIndexFloor);
                const isSelected = selectedId === shape.id || Boolean(selectedShapeIds?.has(shape.id));

                if (shape instanceof UmlPackageShape) {
                    packageElements.push(
                        <UmlPackageWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            onShapeCollapseToggled={onShapeCollapseToggled}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                            titleLayerNode={titleLayerNode}
                        />,
                    );
                    // Collapsed package hides everything inside it — children simply
                    // aren't walked/rendered at all (not just visually shrunk, unlike a
                    // collapsed class which keeps rendering its own smaller box).
                    if (!shape.collapsed) {
                        walk(shape.children, stackIndex + 1);
                    }
                    return;
                }

                if (shape instanceof UmlClassShape) {
                    otherElements.push(
                        <UmlClassWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            onShapeCollapseToggled={onShapeCollapseToggled}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                        />,
                    );
                    return;
                }

                if (shape instanceof UmlCommentShape) {
                    otherElements.push(
                        <UmlCommentWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                        />,
                    );
                    return;
                }

                if (shape instanceof UmlIconShape) {
                    otherElements.push(
                        <UmlIconWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            onShapeIconKindChanged={onShapeIconKindChanged}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                        />,
                    );
                    return;
                }

                if (shape instanceof UmlSeqParticipantShape) {
                    otherElements.push(
                        <UmlSeqParticipantWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                        />,
                    );
                    return;
                }

                if (shape instanceof UmlSeqLifelineShape) {
                    otherElements.push(
                        <UmlSeqLifelineWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                        />,
                    );
                    return;
                }

                if (shape instanceof UmlSeqMessageShape) {
                    otherElements.push(
                        <UmlSeqMessageWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onConnectorDragStart={onConnectorDragStart}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                            onSeqMessageLabelMoved={onSeqMessageLabelMoved}
                        />,
                    );
                    return;
                }

                if (shape instanceof UmlSeqActivationShape) {
                    // No onConnectorDragStart — Ctrl+drag on an activation bar means
                    // "group-move its nested calls" (see CsSeqDiaRelationHandler.
                    // cascadeNestedSeqDiaShift), not "start drawing a connector". The
                    // connector-draw special-case in handleDragMouseDownHelper only
                    // fires when onConnectorDragStart is actually provided.
                    otherElements.push(
                        <UmlSeqActivationWidget
                            key={shape.id}
                            shape={shape}
                            onShapeMoved={moveShape}
                            onShapeResized={resizeShape}
                            onShapeRenamed={renameShape}
                            isSelected={isSelected}
                            onShapeSelected={selectShape}
                            onShapeShiftSelected={onShapeShiftSelected}
                            selectedShapeIds={selectedShapeIds}
                            getShapeById={getShapeById}
                            onBringToFront={onBringToFront}
                            toWorldPoint={toWorldPoint}
                            draggingShapeIdRef={draggingShapeIdRef}
                            stackIndex={stackIndex}
                        />,
                    );
                }
            });
        }

        walk(shapes, parentZIndex);
        return { packageElements, otherElements };
    }
}

export default ShapeTreeRenderer;
